import posthog from 'posthog-js';

const POSTHOG_KEY = process.env.REACT_APP_POSTHOG_KEY;
const POSTHOG_HOST = process.env.REACT_APP_POSTHOG_HOST || 'https://e.trillboards.com';

let isInitialized = false;

export function initPostHog() {
  if (isInitialized || !POSTHOG_KEY) {
    return;
  }

  posthog.init(POSTHOG_KEY, {
    api_host: POSTHOG_HOST,
    ui_host: 'https://us.posthog.com', // For toolbar/dashboard links
    // Kiosk-specific settings: disable session recording and autocapture
    autocapture: false, // No user clicks to capture on kiosk
    capture_pageview: false, // We'll capture custom events instead
    disable_session_recording: true, // No session recording for kiosk
    persistence: 'localStorage',
    // Screens are devices, not people — don't create/maintain person profiles for
    // anonymous device events (defensive; identify is also deduped in identifyDevice).
    person_profiles: 'identified_only',
    loaded: (posthog) => {
      if (process.env.NODE_ENV === 'development') {
        posthog.debug();
      }
    },
  });

  isInitialized = true;
}

export function identifyDevice(fingerprint, properties = {}) {
  if (!isInitialized || !fingerprint) return;

  // Identify at most once per (fingerprint, screenId). This runs on every
  // (re)connect (QRCode.js + screenEvents.connected) and screens reconnect/reload
  // constantly (watchdog, crashes); re-identifying each time fired a $set person-
  // property write — ~759K/30d, the #1 PostHog volume driver. Keying on screenId
  // too ensures a re-pair to a NEW screen still re-identifies once: a deleted
  // screen keeps the same fingerprint but is assigned a new screenId, and we must
  // refresh the person profile to that new screen. The marker is persisted in
  // localStorage so it survives the frequent reloads an in-memory guard would not.
  const identifyMarker = `${fingerprint}:${properties.screenId || ''}`;
  try {
    if (localStorage.getItem('ph_identified') === identifyMarker) return;
  } catch (_) {
    // localStorage unavailable — fall through and identify once this session.
  }

  posthog.identify(fingerprint, {
    ...properties,
    deviceType: 'screen',
  });

  try {
    localStorage.setItem('ph_identified', identifyMarker);
  } catch (_) {
    // best-effort — worst case we identify again next load
  }
}

// All screen operational events (ad impressions, VAST, playback) are already
// tracked in PG. Only capture events with product-analytics value.
const SCREEN_POSTHOG_ALLOWLIST = {
  'screen_error': true,
};

export function captureEvent(eventName, properties = {}) {
  if (!isInitialized) return;
  // Gate: only send allowlisted events to PostHog
  if (!SCREEN_POSTHOG_ALLOWLIST[eventName]) return;

  posthog.capture(eventName, properties);
}

// Device context for error telemetry
export function getDeviceContext() {
  const ua = navigator.userAgent;
  const uaLower = ua.toLowerCase();

  return {
    // Device type detection
    is_ctv: window.__TRILL_IS_CTV__ || false,
    is_tv: uaLower.includes('tv') || uaLower.includes('tizen') || uaLower.includes('webos'),
    is_fire_tv: /silk|AFT[A-Z]/i.test(ua),  // AFT followed by any letter covers all Fire TV models
    is_samsung_tv: /tizen|smart-tv/i.test(ua),
    is_lg_tv: /webos/i.test(ua),
    is_roku: /roku/i.test(ua),
    is_chromecast: /crkey/i.test(ua),
    is_android_webview: uaLower.includes('wv'),
    is_android: uaLower.includes('android'),

    // Browser/OS
    user_agent: ua,
    platform: navigator.platform,

    // Screen info
    screen_width: window.screen?.width,
    screen_height: window.screen?.height,
    device_pixel_ratio: window.devicePixelRatio,

    // Identity
    fingerprint: localStorage.getItem('fingerprint'),
    screen_id: localStorage.getItem('screenId')
      || window.__TRILLBOARD_SCREEN_ID__
      || window.myId
      || null,

    // Performance (if available - Chrome/Edge only)
    memory_used_mb: performance?.memory?.usedJSHeapSize
      ? Math.round(performance.memory.usedJSHeapSize / 1048576)
      : null,
    memory_total_mb: performance?.memory?.totalJSHeapSize
      ? Math.round(performance.memory.totalJSHeapSize / 1048576)
      : null,
  };
}

// Error capture for global error handler with device context
export function captureError(error, properties = {}) {
  if (!isInitialized) return;

  const deviceContext = getDeviceContext();

  const errorData = {
    message: error?.message || String(error),
    stack: error?.stack,
    name: error?.name,
    ...deviceContext,
    ...properties,
  };

  posthog.capture('screen_error', errorData);
}

// Screen-specific events
export const screenEvents = {
  connected: (fingerprint, screenId, properties = {}) => {
    identifyDevice(fingerprint, { screenId });
    captureEvent('screen_connected', { fingerprint, screenId, ...properties });
  },
  adImpression: (adId, screenId, properties = {}) => {
    captureEvent('ad_impression', { adId, screenId, ...properties });
  },
  adCompleted: (adId, screenId, duration, properties = {}) => {
    captureEvent('ad_completed', { adId, screenId, duration, ...properties });
  },
  videoPlaybackStarted: (contentId, screenId, properties = {}) => {
    captureEvent('video_playback_started', { contentId, screenId, ...properties });
  },
  videoPlaybackEnded: (contentId, screenId, properties = {}) => {
    captureEvent('video_playback_ended', { contentId, screenId, ...properties });
  },
  qrCodeDisplayed: (screenId, properties = {}) => {
    captureEvent('qr_code_displayed', { screenId, ...properties });
  },
  // VAST fill confirmation (client-side — adds device memory/browser context)
  vastFilled: (screenId, partnerName, creativeDuration, properties = {}) => {
    const ctx = getDeviceContext();
    captureEvent('vast_filled', {
      screenId,
      partner_name: partnerName,
      creative_duration: creativeDuration,
      memory_used_mb: ctx.memory_used_mb,
      memory_total_mb: ctx.memory_total_mb,
      ...properties,
    });
  },
  // VAST error (client-side — adds device memory/browser context)
  vastError: (screenId, partnerName, errorCode, properties = {}) => {
    const ctx = getDeviceContext();
    captureEvent('vast_error', {
      screenId,
      partner_name: partnerName,
      error_code: errorCode,
      memory_used_mb: ctx.memory_used_mb,
      memory_total_mb: ctx.memory_total_mb,
      ...properties,
    });
  },
};

// Feature flag helpers (for screen app experiments)
export function isFeatureEnabled(flagName) {
  if (!isInitialized) return false;
  return posthog.isFeatureEnabled(flagName) ?? false;
}

export function getFeatureFlag(flagName) {
  if (!isInitialized) return undefined;
  return posthog.getFeatureFlag(flagName);
}

export function onFeatureFlags(callback) {
  if (!isInitialized) return;
  posthog.onFeatureFlags(callback);
}

export { posthog };
